import {
  Activity,
  BarChart3,
  BrainCircuit,
  Circle,
  ClipboardList,
  Cpu,
  FileBarChart,
  FileText,
  IndianRupee,
  LayoutDashboard,
  Library,
  ListChecks,
  MessageSquare,
  Network,
  Ruler,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { Badge, cn, focusRing } from '@insightai/ui'
import { NavLink } from 'react-router-dom'
import { useRBAC, useWorkspaceScope } from '../../shared/useRBAC'
import { visibleNavGroups, type NavItem } from '../navigation'

/**
 * F0-21 / PE-01 — explicit icon map, NOT a namespace import.
 *
 * This was originally `import * as Icons from 'lucide-react'`, because the
 * sidebar resolves icons from string names in navigation.ts. Measured cost of
 * that convenience: 825KB raw / ~160KB gzipped of icon modules in a chunk the
 * browser fetches before first paint — on its own, ~80% of the entire 200KB
 * budget for a sidebar with twenty icons.
 *
 * The map costs one line per nav item and is enforced by types: a `NavItem.icon`
 * naming an unmapped icon falls back to `Circle` rather than silently importing
 * 1,500 modules.
 */
const ICONS: Record<string, LucideIcon> = {
  Activity,
  BarChart3,
  BrainCircuit,
  ClipboardList,
  Cpu,
  FileBarChart,
  FileText,
  IndianRupee,
  LayoutDashboard,
  Library,
  ListChecks,
  MessageSquare,
  Network,
  Ruler,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Users,
}

function itemHref(workspaceId: string | null, item: NavItem) {
  if (item.to.startsWith('/ops') || item.to.startsWith('/admin')) return item.to
  return `/w/${workspaceId ?? ''}${item.to}`
}

export function Sidebar({ className }: { className?: string }) {
  const { workspaceId } = useWorkspaceScope()
  const { roles, isMethodologyUser, isResearchOps, isPlatformAdmin } = useRBAC()

  const groups = visibleNavGroups({
    roles,
    isMethodologyUser,
    isResearchOps,
    isPlatformAdmin,
  })

  return (
    <nav
      aria-label="Main navigation"
      className={cn(
        'flex w-64 shrink-0 flex-col gap-6 overflow-y-auto border-e border-border-subtle bg-surface-raised p-4',
        className,
      )}
    >
      {groups.map((group) => (
        <div key={group.id} className="flex flex-col gap-1">
          <h2 className="px-2 pb-1 text-micro uppercase tracking-wide text-text-tertiary">
            {group.label}
          </h2>
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const Icon = ICONS[item.icon] ?? Circle
              const unavailable = Boolean(item.phase && item.phase > 1)

              if (unavailable) {
                return (
                  <li key={item.id}>
                    <span
                      aria-disabled="true"
                      title={`Arrives in Phase ${item.phase}`}
                      className="flex cursor-not-allowed items-center gap-2.5 rounded-md px-2 py-2 text-body-sm text-text-tertiary"
                    >
                      <Icon aria-hidden className="size-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                      <Badge tone="neutral" className="ms-auto shrink-0">
                        P{item.phase}
                      </Badge>
                    </span>
                  </li>
                )
              }

              return (
                <li key={item.id}>
                  <NavLink
                    to={itemHref(workspaceId, item)}
                    end={item.to === ''}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-md px-2 py-2 text-body-sm transition-colors duration-fast',
                        isActive
                          ? 'bg-surface-selected font-medium text-text-primary'
                          : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                        focusRing,
                      )
                    }
                  >
                    <Icon aria-hidden className="size-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}
